import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap, Handle, Position,
  useNodesState, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../../contexts/AuthContext';

// --- Catalogo de tipos de no (RFC-001 secao 5) ------------------------------
const TIPOS = {
  inicio:   { label: 'Inicio',   cor: '#6c757d', saida: 'next',   desc: 'Ponto de entrada do fluxo. So existe um. Aponta para o primeiro no.' },
  mensagem: { label: 'Mensagem', cor: '#0d6efd', saida: 'next',   desc: 'Envia um texto ao cliente e segue em frente. Aceita {{variavel}} no texto.' },
  pergunta: { label: 'Pergunta', cor: '#6610f2', saida: 'next',   desc: 'Faz uma pergunta, espera a resposta livre do cliente e guarda numa variavel.' },
  opcoes:   { label: 'Opcoes',   cor: '#fd7e14', saida: 'opcoes', desc: 'Mostra um menu numerado e segue por um caminho diferente conforme a escolha.' },
  condicao: { label: 'Condicao', cor: '#198754', saida: 'cond',   desc: 'Decide o caminho (sim/nao) comparando uma variavel ou o contexto (horario, conversa).' },
  acao:     { label: 'Acao',     cor: '#20c997', saida: 'next',   desc: 'Executa um efeito do sistema: concluir coleta, passar para atendente, categorizar, etc.' },
  subfluxo: { label: 'Subfluxo', cor: '#0dcaf0', saida: 'next',   desc: 'Chama outro fluxo reutilizavel e retoma aqui quando ele termina.' },
  handoff:  { label: 'Handoff',  cor: '#dc3545', saida: null,     desc: 'Encerra o bot e entrega a conversa para um atendente humano. No final.' },
  fim:      { label: 'Fim',      cor: '#212529', saida: null,     desc: 'Termina o fluxo em silencio. No final.' },
};
const SINGLE_OUT = ['inicio', 'mensagem', 'pergunta', 'acao', 'subfluxo'];
const OPERADORES = ['==', '!=', 'contem', 'vazio', 'preenchido'];
const ACOES = ['marcarColetaConcluida', 'handoffAtendente', 'atribuirAtendente', 'encaminharFila', 'coletarVariavel', 'categorizarDescricao'];

// --- Conversao definicao <-> React Flow -------------------------------------
function defParaNodes(definicao, startNodeId) {
  return (definicao.nodes || []).map((n, idx) => ({
    id: n.id,
    type: 'flow',
    position: (n.ui && typeof n.ui.x === 'number')
      ? { x: n.ui.x, y: n.ui.y }
      : { x: 120 + (idx % 4) * 230, y: 80 + Math.floor(idx / 4) * 150 },
    data: {
      tipo: n.tipo,
      nome: n.nome || '',
      config: n.config ? { ...n.config } : {},
      next: n.next ?? null,
      then: n.then ?? null,
      else: n.else ?? null,
      isStart: n.id === startNodeId,
    },
  }));
}

function nodesParaDef(nodes, startNodeId, variaveis) {
  const defNodes = nodes.map((n) => {
    const d = n.data;
    const base = { id: n.id, tipo: d.tipo, ui: { x: Math.round(n.position.x), y: Math.round(n.position.y) } };
    if (d.nome) base.nome = d.nome;
    if (d.tipo === 'condicao') {
      base.config = d.config || {};
      base.then = d.then || null;
      base.else = d.else || null;
    } else if (d.tipo === 'opcoes') {
      base.config = d.config || { opcoes: [] };
    } else {
      if (d.config && Object.keys(d.config).length) base.config = d.config;
      if (SINGLE_OUT.includes(d.tipo)) base.next = d.next || null;
    }
    return base;
  });
  return {
    start_node_id: startNodeId || (defNodes[0] && defNodes[0].id) || null,
    variaveis: variaveis || [],
    nodes: defNodes,
  };
}

function construirEdges(nodes) {
  const ids = new Set(nodes.map((n) => n.id));
  const edges = [];
  const add = (source, handle, target, label) => {
    if (target && ids.has(target)) {
      edges.push({
        id: `${source}__${handle}`, source, sourceHandle: handle, target,
        label, markerEnd: { type: MarkerType.ArrowClosed },
      });
    }
  };
  nodes.forEach((n) => {
    const d = n.data;
    if (SINGLE_OUT.includes(d.tipo)) add(n.id, 'next', d.next);
    else if (d.tipo === 'condicao') { add(n.id, 'then', d.then, 'sim'); add(n.id, 'else', d.else, 'nao'); }
    else if (d.tipo === 'opcoes') (d.config?.opcoes || []).forEach((o, i) => add(n.id, `op-${i}`, o.next, o.rotulo));
  });
  return edges;
}

function listaHtml(titulo, arr, cls) {
  return `<b class="${cls}">${titulo} (${arr.length})</b>`
    + `<ul style="text-align:left;max-height:260px;overflow:auto;font-size:.85rem">`
    + arr.map((x) => `<li>${x}</li>`).join('') + '</ul>';
}

function mostrarValidacao({ erros = [], avisos = [] }) {
  const html = (erros.length ? listaHtml('Erros', erros, 'text-danger') : '')
    + (avisos.length ? listaHtml('Avisos', avisos, 'text-warning') : '')
    + (!erros.length && !avisos.length ? '<span class="text-success">Tudo certo! Nenhum problema encontrado.</span>' : '');
  Swal.fire({
    icon: erros.length ? 'error' : (avisos.length ? 'warning' : 'success'),
    title: 'Validacao do fluxo', html, width: 600,
  });
}

// Extrai os ids de no das mensagens de erro (formato "[tipo id] ...") para
// destacar os nos no canvas. Erros sem no associado vao para `gerais`.
function extrairIdsComErro(erros) {
  const ids = new Set();
  const gerais = [];
  (erros || []).forEach((e) => {
    const m = /^\[[a-z]+ ([^\]]+)\]/i.exec(String(e));
    if (m) ids.add(m[1]); else gerais.push(String(e));
  });
  return { ids, gerais };
}

function resumoNo(d) {
  if (d.tipo === 'mensagem' || d.tipo === 'pergunta') return d.config?.texto || '—';
  if (d.tipo === 'opcoes') return `${(d.config?.opcoes || []).length} opcao(oes)`;
  if (d.tipo === 'condicao') return `${d.config?.esquerda || '?'} ${d.config?.operador || ''} ${d.config?.direita || ''}`;
  if (d.tipo === 'acao') return d.config?.acao || '(sem acao)';
  if (d.tipo === 'subfluxo') return d.config?.fluxo_versao_id ? `v#${d.config.fluxo_versao_id}` : '(escolher)';
  return '';
}

// --- No customizado ---------------------------------------------------------
function FlowNode({ data, selected }) {
  const meta = TIPOS[data.tipo] || { label: data.tipo, cor: '#888' };
  const terminal = !meta.saida;
  return (
    <div title={meta.desc} style={{
      borderRadius: 10,
      border: `2px solid ${data.erro ? '#dc3545' : (selected ? 'var(--portal-blue-soft)' : meta.cor)}`,
      background: 'var(--portal-surface)', color: 'var(--portal-text)',
      minWidth: 156, maxWidth: 210, fontSize: 12,
      boxShadow: data.erro
        ? '0 0 0 3px rgba(220,53,69,.4)'
        : (selected ? '0 0 0 3px rgba(60,168,188,.25)' : '0 4px 12px rgba(0,0,0,.45)'),
    }}>
      {data.tipo !== 'inicio' && <Handle type="target" position={Position.Top} />}
      <div style={{ background: meta.cor, color: '#fff', padding: '4px 9px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 11, letterSpacing: '.02em', display: 'flex', justifyContent: 'space-between' }}>
        <span>{meta.label.toUpperCase()}</span>
        <span>
          {data.erro && <span title="Este no tem erro de validacao" style={{ marginRight: 4 }}>⚠</span>}
          {data.isStart && <span title="No inicial">★</span>}
        </span>
      </div>
      <div style={{ padding: '7px 9px', color: 'var(--portal-text)', wordBreak: 'break-word', lineHeight: 1.35 }}>
        {data.nome && <div style={{ fontWeight: 700, marginBottom: 2 }}>{data.nome}</div>}
        <div style={{ color: data.nome ? 'var(--portal-muted)' : 'var(--portal-text)' }}>
          {resumoNo(data) || <span style={{ color: 'var(--portal-muted)' }}>—</span>}
        </div>
      </div>

      {SINGLE_OUT.includes(data.tipo) && <Handle type="source" id="next" position={Position.Bottom} />}
      {data.tipo === 'condicao' && (
        <>
          <Handle type="source" id="then" position={Position.Bottom} style={{ left: '30%' }} />
          <span className="fx-handle-lbl" style={{ left: '30%' }}>sim</span>
          <Handle type="source" id="else" position={Position.Bottom} style={{ left: '70%' }} />
          <span className="fx-handle-lbl" style={{ left: '70%' }}>nao</span>
        </>
      )}
      {data.tipo === 'opcoes' && (data.config?.opcoes || []).map((o, i, arr) => {
        const left = ((i + 1) / (arr.length + 1)) * 100;
        return (
          <React.Fragment key={i}>
            <Handle type="source" id={`op-${i}`} position={Position.Bottom} style={{ left: `${left}%` }} />
            <span className="fx-handle-lbl" style={{ left: `${left}%` }}>{o.rotulo || (i + 1)}</span>
          </React.Fragment>
        );
      })}
      {terminal && <div style={{ height: 4 }} />}
    </div>
  );
}

const nodeTypes = { flow: FlowNode };

const EDITOR_CSS = `
.fluxo-editor { background: var(--portal-bg); color: var(--portal-text); }
.fluxo-editor .fx-bar { background: var(--portal-sidebar); border-bottom: 1px solid var(--portal-border); }
.fluxo-editor .fx-side { background: var(--portal-surface); }
.fluxo-editor .fx-paleta { border-right: 1px solid var(--portal-border); }
.fluxo-editor .fx-painel { border-left: 1px solid var(--portal-border); }
.fluxo-editor .fx-canvas, .fluxo-editor .react-flow { background: var(--portal-bg); }

.fluxo-editor .fx-section-label { color: var(--portal-muted); font-size:.7rem; text-transform:uppercase; letter-spacing:.05em; font-weight:700; margin-bottom:6px; }
.fluxo-editor .fx-divider { height:1px; background: var(--portal-border); margin:10px 0; }
.fluxo-editor hr { border-color: var(--portal-border); opacity:.6; }
.fluxo-editor .fx-hint { color: var(--portal-muted); font-size:.82rem; line-height:1.5; background: var(--portal-surface-soft); border:1px dashed var(--portal-border); border-radius:10px; padding:10px; }
.fluxo-editor .fx-hint strong { color: var(--portal-text); }

.fluxo-editor .fx-paleta-btn { border:none; color:#fff; border-radius:8px; font-weight:600; font-size:.78rem; padding:6px 8px; text-align:left; opacity:.92; transition: opacity .15s, transform .05s; }
.fluxo-editor .fx-paleta-btn:hover { opacity:1; }
.fluxo-editor .fx-paleta-btn:active { transform: scale(.98); }

.fluxo-editor .fx-btn-ghost { background: var(--portal-surface-soft); color: var(--portal-text); border:1px solid var(--portal-border); }
.fluxo-editor .fx-btn-ghost:hover { background: var(--portal-surface); color: var(--portal-text); }
.fluxo-editor .fx-btn-save { background: var(--portal-blue); color:#fff; border:none; }
.fluxo-editor .fx-btn-save:hover { filter:brightness(1.1); color:#fff; }
.fluxo-editor .fx-btn-pub { background: linear-gradient(135deg, var(--portal-green) 0%, #2f9e3c 100%); color:#fff; border:none; }
.fluxo-editor .fx-btn-pub:hover { filter:brightness(1.08); color:#fff; }

.fluxo-editor label, .fluxo-editor .form-label { color: var(--portal-muted); font-weight:600; font-size:.76rem; margin-bottom:2px; }
.fluxo-editor .text-muted { color: var(--portal-muted) !important; }
.fluxo-editor code { color: var(--portal-blue-soft); }
.fluxo-editor input, .fluxo-editor textarea, .fluxo-editor select {
  background: var(--portal-surface-soft); color: var(--portal-text);
  border:1px solid var(--portal-border); border-radius:8px;
}
.fluxo-editor input::placeholder, .fluxo-editor textarea::placeholder { color: var(--portal-muted); }
.fluxo-editor input:focus, .fluxo-editor textarea:focus, .fluxo-editor select:focus {
  background: var(--portal-surface-soft); color: var(--portal-text);
  border-color: var(--portal-blue-soft); box-shadow:0 0 0 3px rgba(60,168,188,.18);
}
.fluxo-editor .border { border-color: var(--portal-border) !important; }

.fluxo-editor .react-flow__handle { background: var(--portal-blue-soft); border:2px solid var(--portal-surface); width:9px; height:9px; }
.fluxo-editor .react-flow__edge-path { stroke: var(--portal-muted); stroke-width:1.5; }
.fluxo-editor .react-flow__edge.selected .react-flow__edge-path { stroke: var(--portal-blue-soft); }
.fluxo-editor .react-flow__edge-text { fill: var(--portal-text); font-size:10px; }
.fluxo-editor .react-flow__edge-textbg { fill: var(--portal-surface); }
.fluxo-editor .react-flow__controls button { background: var(--portal-surface); border-bottom:1px solid var(--portal-border); color: var(--portal-text); }
.fluxo-editor .react-flow__controls button:hover { background: var(--portal-surface-soft); }
.fluxo-editor .react-flow__controls button svg { fill: var(--portal-text); }
.fluxo-editor .react-flow__minimap { background: var(--portal-surface) !important; border:1px solid var(--portal-border); border-radius:8px; }
.fluxo-editor .react-flow__attribution { display:none; }
.fluxo-editor .fx-handle-lbl { position:absolute; bottom:-15px; transform:translateX(-50%); font-size:9px; line-height:1; color:var(--portal-muted); background:var(--portal-surface); padding:1px 3px; border-radius:4px; white-space:nowrap; pointer-events:none; max-width:64px; overflow:hidden; text-overflow:ellipsis; }

.fluxo-editor .fx-sim { position:absolute; top:46px; right:0; bottom:0; width:340px; background:var(--portal-surface); border-left:1px solid var(--portal-border); display:flex; flex-direction:column; z-index:30; box-shadow:-10px 0 30px rgba(0,0,0,.4); }
.fluxo-editor .fx-sim-head { display:flex; justify-content:space-between; align-items:center; padding:8px 12px; border-bottom:1px solid var(--portal-border); color:var(--portal-text); }
.fluxo-editor .fx-sim-opts { display:flex; flex-wrap:wrap; gap:8px 12px; padding:8px 12px; border-bottom:1px solid var(--portal-border); font-size:.74rem; color:var(--portal-muted); align-items:center; }
.fluxo-editor .fx-sim-opts label { display:flex; align-items:center; gap:4px; margin:0; cursor:pointer; }
.fluxo-editor .fx-sim-hint { padding:7px 12px; font-size:.72rem; color:var(--portal-muted); border-bottom:1px solid var(--portal-border); line-height:1.4; }
.fluxo-editor .fx-sim-msgs { flex:1; overflow-y:auto; padding:12px; display:flex; flex-direction:column; gap:8px; }
.fluxo-editor .fx-sim-input { display:flex; gap:6px; padding:10px; border-top:1px solid var(--portal-border); }
.fluxo-editor .fx-sim-input input { flex:1; }
.fluxo-editor .fx-msg { max-width:85%; padding:7px 10px; border-radius:10px; font-size:.82rem; white-space:pre-wrap; word-break:break-word; line-height:1.36; }
.fluxo-editor .fx-msg-bot { align-self:flex-start; background:var(--portal-surface-soft); color:var(--portal-text); border:1px solid var(--portal-border); }
.fluxo-editor .fx-msg-user { align-self:flex-end; background:var(--portal-blue); color:#fff; }
.fluxo-editor .fx-msg-anotacao { align-self:center; background:transparent; color:var(--portal-muted); font-style:italic; font-size:.74rem; border:1px dashed var(--portal-border); padding:4px 8px; }
.fluxo-editor .fx-msg-erro { align-self:center; background:rgba(220,53,69,.15); color:#ff8787; border:1px solid rgba(220,53,69,.45); }
.fluxo-editor .fx-sim-end { align-self:center; color:var(--portal-muted); font-size:.74rem; padding:2px; }
`;

// --- Editor principal -------------------------------------------------------
export default function FluxoEditor({ fluxoId, onBack }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [startNodeId, setStartNodeId] = useState(null);
  const [variaveis, setVariaveis] = useState([]);
  const [fluxo, setFluxo] = useState(null);
  const [reutilizaveis, setReutilizaveis] = useState([]);
  const [atendentes, setAtendentes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  // Simulador (dry-run) embutido
  const [simOpen, setSimOpen] = useState(false);
  const [simMsgs, setSimMsgs] = useState([]);
  const [simEstado, setSimEstado] = useState(null);
  const [simInput, setSimInput] = useState('');
  const [simBusy, setSimBusy] = useState(false);
  const [simEncerrado, setSimEncerrado] = useState(false);
  const [simOpts, setSimOpts] = useState({ dentroDoHorario: true, conversaEncerrada: false, temChamado: false });
  const [errosGerais, setErrosGerais] = useState([]); // erros de validacao sem no associado
  const [dirty, setDirty] = useState(false);          // ha alteracoes nao salvas
  const primeiraAlteracaoRef = useRef(true);          // ignora o 1o ciclo pos-carga

  const edges = useMemo(() => construirEdges(nodes), [nodes]);
  const selecionado = nodes.find((n) => n.id === selectedId) || null;
  const nodeOptions = nodes.map((n) => n.id);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r, rr, ra] = await Promise.all([
          axios.get(`${API}/atendimento/fluxos/${fluxoId}`),
          axios.get(`${API}/atendimento/fluxos/reutilizaveis`),
          axios.get(`${API}/atendimento/fluxos/atendentes`).catch(() => ({ data: { data: [] } })),
        ]);
        const { fluxo: f, definicao } = r.data.data;
        setFluxo(f);
        setStartNodeId(definicao.start_node_id || null);
        setNodes(defParaNodes(definicao, definicao.start_node_id));
        setVariaveis(definicao.variaveis || []);
        setReutilizaveis((rr.data.data || []).filter((x) => x.fluxo_id !== Number(fluxoId)));
        setAtendentes(ra.data.data || []);
      } catch (e) {
        Swal.fire('Erro', 'Falha ao carregar o fluxo.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [fluxoId, setNodes]);

  // Atualiza dados de um no.
  const patchNode = useCallback((id, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch, erro: false } } : n)));
  }, [setNodes]);
  const patchConfig = useCallback((id, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, config: { ...n.data.config, ...patch }, erro: false } } : n)));
  }, [setNodes]);

  const onConnect = useCallback((c) => {
    setNodes((ns) => ns.map((n) => {
      if (n.id !== c.source) return n;
      const d = { ...n.data };
      // React Flow as vezes entrega sourceHandle vazio ao arrastar de um no de
      // saida unica; nesse caso assume 'next' (senao a aresta nao era criada).
      const sh = c.sourceHandle || (SINGLE_OUT.includes(d.tipo) ? 'next' : c.sourceHandle);
      if (sh === 'next') d.next = c.target;
      else if (sh === 'then') d.then = c.target;
      else if (sh === 'else') d.else = c.target;
      else if (sh && sh.startsWith('op-')) {
        const i = Number(sh.slice(3));
        const ops = [...(d.config.opcoes || [])];
        if (ops[i]) { ops[i] = { ...ops[i], next: c.target }; d.config = { ...d.config, opcoes: ops }; }
      }
      return { ...n, data: d };
    }));
  }, [setNodes]);

  const limparRef = useCallback((source, handle) => {
    setNodes((ns) => ns.map((n) => {
      if (n.id !== source) return n;
      const d = { ...n.data };
      if (handle === 'next') d.next = null;
      else if (handle === 'then') d.then = null;
      else if (handle === 'else') d.else = null;
      else if (handle?.startsWith('op-')) {
        const i = Number(handle.slice(3));
        const ops = [...(d.config.opcoes || [])];
        if (ops[i]) { ops[i] = { ...ops[i], next: null }; d.config = { ...d.config, opcoes: ops }; }
      }
      return { ...n, data: d };
    }));
  }, [setNodes]);

  const onEdgesChange = useCallback((changes) => {
    changes.forEach((ch) => {
      if (ch.type === 'remove') {
        const [source, handle] = ch.id.split('__');
        limparRef(source, handle);
      }
    });
  }, [limparRef]);

  // Limpa referencias pendentes ao apagar nos.
  const onNodesDelete = useCallback((deleted) => {
    const mortos = new Set(deleted.map((n) => n.id));
    setNodes((ns) => ns.map((n) => {
      const d = { ...n.data };
      if (mortos.has(d.next)) d.next = null;
      if (mortos.has(d.then)) d.then = null;
      if (mortos.has(d.else)) d.else = null;
      if (d.config?.opcoes) d.config = { ...d.config, opcoes: d.config.opcoes.map((o) => (mortos.has(o.next) ? { ...o, next: null } : o)) };
      return { ...n, data: d };
    }));
  }, [setNodes]);

  const addNode = (tipo) => {
    const id = `${tipo}_${Math.random().toString(36).slice(2, 7)}`;
    const novo = {
      id, type: 'flow',
      position: { x: 200 + Math.random() * 120, y: 200 + Math.random() * 120 },
      data: {
        tipo, next: null, then: null, else: null, isStart: false,
        config: tipo === 'opcoes' ? { texto: '', opcoes: [], invalido: { texto: '' } }
          : tipo === 'condicao' ? { esquerda: '', operador: '==', direita: '' }
            : tipo === 'acao' ? { acao: ACOES[0], params: {} }
              : {},
      },
    };
    setNodes((ns) => [...ns, novo]);
    setSelectedId(id);
  };

  const definirInicio = (id) => {
    setStartNodeId(id);
    setNodes((ns) => ns.map((n) => ({ ...n, data: { ...n.data, isStart: n.id === id } })));
  };

  const salvarRascunho = useCallback(async (silencioso = false) => {
    const definicao = nodesParaDef(nodes, startNodeId, variaveis);
    await axios.put(`${API}/atendimento/fluxos/${fluxoId}/rascunho`, { definicao });
    setDirty(false);
    if (!silencioso) Swal.fire({ icon: 'success', title: 'Rascunho salvo', timer: 1200, showConfirmButton: false });
  }, [nodes, startNodeId, variaveis, fluxoId]);

  // Marca alteracoes nao salvas e faz autosave (debounce) do rascunho.
  useEffect(() => {
    if (loading) return undefined;
    if (primeiraAlteracaoRef.current) { primeiraAlteracaoRef.current = false; return undefined; }
    setDirty(true);
    const tmr = setTimeout(() => { salvarRascunho(true).catch(() => {}); }, 4000);
    return () => clearTimeout(tmr);
  }, [nodes, startNodeId, variaveis, loading, salvarRascunho]);

  // Avisa antes de fechar/atualizar a aba com alteracoes nao salvas.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const salvar = async () => {
    setSalvando(true);
    try { await salvarRascunho(); }
    catch (e) { Swal.fire('Erro', e.response?.data?.message || 'Falha ao salvar rascunho.', 'error'); }
    finally { setSalvando(false); }
  };

  // Marca os nos com erro no canvas e guarda os erros gerais (sem no).
  const aplicarErros = (erros) => {
    const { ids, gerais } = extrairIdsComErro(erros);
    setNodes((ns) => ns.map((n) => ({ ...n, data: { ...n.data, erro: ids.has(n.id) } })));
    setErrosGerais(gerais);
  };

  const validar = async () => {
    setSalvando(true);
    try {
      await salvarRascunho(true);
      const r = await axios.post(`${API}/atendimento/fluxos/${fluxoId}/validar`);
      aplicarErros(r.data.data?.erros || []);
      mostrarValidacao(r.data.data);
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao validar.', 'error');
    } finally { setSalvando(false); }
  };

  const publicar = async () => {
    setSalvando(true);
    try {
      await salvarRascunho(true);
      const r = await axios.post(`${API}/atendimento/fluxos/${fluxoId}/publicar`);
      const avisos = r.data.data?.avisos || [];
      aplicarErros([]);
      Swal.fire({
        icon: 'success',
        title: `Publicado (v${r.data.data.versao})`,
        html: avisos.length ? listaHtml('Avisos', avisos, 'text-warning') : 'Disponivel para atribuir a canais em Admin &gt; Canais WhatsApp.',
        width: 600,
      });
    } catch (e) {
      const d = e.response?.data;
      if (d?.data?.erros) { aplicarErros(d.data.erros); mostrarValidacao(d.data); }
      else Swal.fire('Erro', d?.message || 'Falha ao publicar.', 'error');
    } finally { setSalvando(false); }
  };

  // --- Simulador: chama o dry-run no backend (reusa o motor real) ----------
  const simChamar = useCallback(async (estado, texto) => {
    setSimBusy(true);
    try {
      const def = nodesParaDef(nodes, startNodeId, variaveis);
      const r = await axios.post(`${API}/atendimento/fluxos/${fluxoId}/simular`, { definicao: def, estado, texto, opts: simOpts });
      const data = r.data.data || {};
      setSimMsgs((m) => {
        const out = [...m];
        if (texto != null) out.push({ tipo: 'user', texto });
        (data.mensagens || []).forEach((x) => out.push(x));
        return out;
      });
      setSimEstado({ stack: data.stack, dados: data.dados });
      setSimEncerrado(Boolean(data.encerrado));
      if (data.aguardando && data.aguardando.id) setSelectedId(data.aguardando.id);
      if (texto == null && data.encerrado) {
        setSimMsgs((m) => [...m, { tipo: 'anotacao', texto: 'O fluxo terminou sem pedir resposta. Para interagir, adicione um no Pergunta ou Opcoes depois do inicio.' }]);
      }
    } catch (e) {
      const st = e.response?.status;
      const txt = e.response?.data?.message
        || (st === 404
          ? 'Endpoint de simulacao nao encontrado (HTTP 404). Reinicie/atualize o backend para carregar a rota /simular.'
          : `Falha ao simular${st ? ' (HTTP ' + st + ')' : ' \u2014 servidor inacessivel'}.`);
      setSimMsgs((m) => [...m, { tipo: 'erro', texto: txt }]);
    } finally { setSimBusy(false); }
  }, [nodes, startNodeId, variaveis, fluxoId, simOpts]);

  const simIniciar = useCallback(() => {
    setSimMsgs([]); setSimEstado(null); setSimEncerrado(false); setSimInput('');
    simChamar(null, null);
  }, [simChamar]);

  const simEnviar = useCallback(() => {
    const txt = simInput.trim();
    if (!txt || simBusy || simEncerrado) return;
    setSimInput('');
    simChamar(simEstado, txt);
  }, [simInput, simBusy, simEncerrado, simEstado, simChamar]);

  const abrirSim = useCallback(() => {
    setSimOpen(true);
    setSimMsgs([]); setSimEstado(null); setSimEncerrado(false); setSimInput('');
    simChamar(null, null);
  }, [simChamar]);

  if (loading) return <div className="text-center py-5" style={{ color: 'var(--portal-muted)' }}>Carregando editor...</div>;

  return (
    <div className="d-flex flex-column fluxo-editor" style={{ height: 'calc(100vh - 60px)', position: 'relative' }}>
      <style>{EDITOR_CSS}</style>
      {/* Barra superior */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 fx-bar">
        <button type="button" className="btn btn-sm fx-btn-ghost" onClick={onBack}>
          <i className="bi bi-arrow-left" /> Voltar
        </button>
        <strong className="me-auto" style={{ color: 'var(--portal-text)' }}>{fluxo?.nome}</strong>
        <span className="small me-2" style={{ color: 'var(--portal-muted)' }}>{nodes.length} no(s)</span>
        <span className="small me-2" style={{ color: dirty ? '#e0a800' : 'var(--portal-muted)' }}>{dirty ? '\u2022 nao salvo' : 'salvo'}</span>
        <button type="button" className="btn btn-sm fx-btn-ghost" onClick={abrirSim} disabled={salvando}>
          <i className="bi bi-play-circle me-1" /> Testar
        </button>
        <button type="button" className="btn btn-sm fx-btn-ghost" onClick={validar} disabled={salvando}>
          <i className="bi bi-check2-circle me-1" /> Validar
        </button>
        <button type="button" className="btn btn-sm fx-btn-save" onClick={salvar} disabled={salvando}>
          <i className="bi bi-save me-1" /> {salvando ? '...' : 'Salvar'}
        </button>
        <button type="button" className="btn btn-sm fx-btn-pub" onClick={publicar} disabled={salvando}>
          <i className="bi bi-rocket-takeoff me-1" /> Publicar
        </button>
      </div>

      {errosGerais.length > 0 && (
        <div className="px-3 py-1" style={{ background: 'rgba(220,53,69,.12)', borderBottom: '1px solid var(--portal-border)', color: '#ff8787', fontSize: '.78rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />
          {errosGerais.length === 1 ? errosGerais[0] : `${errosGerais.length} problemas gerais — veja o detalhe em Validar.`}
        </div>
      )}

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        {/* Paleta */}
        <div className="fx-side fx-paleta p-2" style={{ width: 140, overflowY: 'auto' }}>
          <div className="fx-section-label">Adicionar no</div>
          {Object.entries(TIPOS).map(([tipo, m]) => (
            <button
              key={tipo} type="button"
              className="fx-paleta-btn w-100 mb-1"
              style={{ background: m.cor }}
              title={m.desc}
              onClick={() => addNode(tipo)}
            >
              + {m.label}
            </button>
          ))}
          <div className="fx-divider" />
          <VariaveisEditor variaveis={variaveis} setVariaveis={setVariaveis} />
        </div>

        {/* Canvas */}
        <div className="flex-grow-1 fx-canvas" style={{ minWidth: 0 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodesDelete={onNodesDelete}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
          >
            <Background color="#243B43" gap={18} />
            <Controls />
            <MiniMap pannable zoomable nodeColor={(n) => (TIPOS[n.data?.tipo] || {}).cor || '#888'} maskColor="rgba(14,27,31,.7)" />
          </ReactFlow>
        </div>

        {/* Painel de propriedades */}
        <div className="fx-side fx-painel p-3" style={{ width: 340, overflowY: 'auto' }}>
          {!selecionado ? (
            <div className="fx-hint">
              <i className="bi bi-hand-index-thumb me-1" />
              Selecione um no para editar. Arraste das <strong>alcas</strong> (bolinhas) para conectar, ou use os campos "proximo no".
            </div>
          ) : (
            <PainelPropriedades
              node={selecionado}
              nodeOptions={nodeOptions}
              variaveis={variaveis}
              reutilizaveis={reutilizaveis}
              atendentes={atendentes}
              startNodeId={startNodeId}
              patchNode={patchNode}
              patchConfig={patchConfig}
              definirInicio={definirInicio}
              removerNode={(id) => { setNodes((ns) => ns.filter((n) => n.id !== id)); onNodesDelete([{ id }]); setSelectedId(null); }}
            />
          )}
        </div>
      </div>

      {simOpen && (
        <div className="fx-sim">
          <div className="fx-sim-head">
            <strong><i className="bi bi-chat-dots me-1" />Simulador</strong>
            <button type="button" className="btn btn-sm fx-btn-ghost" onClick={() => setSimOpen(false)}><i className="bi bi-x-lg" /></button>
          </div>
          <div className="fx-sim-opts">
            <label><input type="checkbox" checked={simOpts.dentroDoHorario} onChange={(e) => setSimOpts((o) => ({ ...o, dentroDoHorario: e.target.checked }))} /> Dentro do horario</label>
            <label><input type="checkbox" checked={simOpts.conversaEncerrada} onChange={(e) => setSimOpts((o) => ({ ...o, conversaEncerrada: e.target.checked }))} /> Conversa encerrada</label>
            <label><input type="checkbox" checked={simOpts.temChamado} onChange={(e) => setSimOpts((o) => ({ ...o, temChamado: e.target.checked }))} /> Tem chamado</label>
            <button type="button" className="btn btn-sm fx-btn-ghost ms-auto" onClick={simIniciar}><i className="bi bi-arrow-clockwise me-1" />Reiniciar</button>
          </div>
          <div className="fx-sim-hint">Alterou o contexto acima? Clique em Reiniciar. Acoes que abrem chamado/atribuem atendente aparecem como anotacao (nao executam de verdade).</div>
          <div className="fx-sim-msgs">
            {simMsgs.map((m, i) => <SimMsg key={i} m={m} />)}
            {simBusy && <div className="fx-sim-end">...</div>}
            {simEncerrado && <div className="fx-sim-end">- fluxo encerrado -</div>}
          </div>
          <div className="fx-sim-input">
            <input
              className="form-control form-control-sm" value={simInput}
              placeholder={simEncerrado ? 'Reinicie para testar de novo' : 'Digite a resposta do cliente...'}
              disabled={simBusy || simEncerrado || !(simEstado?.stack?.length)}
              onChange={(e) => setSimInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') simEnviar(); }}
            />
            <button type="button" className="btn btn-sm fx-btn-save" onClick={simEnviar} disabled={simBusy || simEncerrado || !(simEstado?.stack?.length)}><i className="bi bi-send" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Editor de variaveis ----------------------------------------------------
// Mantem o texto BRUTO em estado local (preserva virgulas/espacos enquanto
// digita) e deriva as variaveis dele. Antes, reconstruir o valor a partir das
// variaveis parseadas descartava a virgula a cada tecla.
function VariaveisEditor({ variaveis, setVariaveis }) {
  const [texto, setTexto] = useState(() => (variaveis || []).map((v) => v.chave).join(', '));
  return (
    <div>
      <div className="fx-section-label">Variaveis</div>
      <textarea
        className="form-control form-control-sm" rows={2} value={texto}
        placeholder="nome, tipo_usuario"
        onChange={(e) => {
          const valor = e.target.value;
          setTexto(valor);
          const chaves = valor.split(',').map((s) => s.trim()).filter(Boolean);
          setVariaveis(chaves.map((chave) => ({ chave })));
        }}
      />
      <small className="text-muted">Separe por virgula (texto)</small>
    </div>
  );
}

// --- Painel de propriedades -------------------------------------------------
function SelectAlvo({ label, valor, opcoes, selfId, onChange }) {
  return (
    <div className="mb-2">
      <label className="form-label small mb-0">{label}</label>
      <select className="form-select form-select-sm" value={valor || ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">(nenhum)</option>
        {opcoes.filter((id) => id !== selfId).map((id) => <option key={id} value={id}>{id}</option>)}
      </select>
    </div>
  );
}

function PainelPropriedades({ node, nodeOptions, reutilizaveis, atendentes, variaveis, startNodeId, patchNode, patchConfig, definirInicio, removerNode }) {
  const { id } = node;
  const d = node.data;
  const cfg = d.config || {};

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="badge text-white" style={{ background: (TIPOS[d.tipo] || {}).cor }}>{(TIPOS[d.tipo] || {}).label}</span>
        <code className="small text-muted">{id}</code>
      </div>

      <div className="mb-2">
        <label className="form-label small mb-0">Nome do no (opcional)</label>
        <input className="form-control form-control-sm" placeholder="ex: Menu inicial" value={d.nome || ''} onChange={(e) => patchNode(id, { nome: e.target.value })} />
      </div>

      <button
        type="button"
        className={`btn btn-sm w-100 mb-2 ${startNodeId === id ? 'btn-warning' : 'btn-outline-warning'}`}
        onClick={() => definirInicio(id)}
      >
        ★ {startNodeId === id ? 'Este e o no inicial' : 'Definir como inicio'}
      </button>

      {(d.tipo === 'mensagem' || d.tipo === 'pergunta') && (
        <div className="mb-2">
          <label className="form-label small mb-0">Texto {'{{variavel}}'}</label>
          <textarea className="form-control form-control-sm" rows={3} value={cfg.texto || ''} onChange={(e) => patchConfig(id, { texto: e.target.value })} />
        </div>
      )}
      {d.tipo === 'pergunta' && (
        <>
          <div className="mb-2">
            <label className="form-label small mb-0">Salva resposta em</label>
            <input className="form-control form-control-sm" value={cfg.salva_em || ''} onChange={(e) => patchConfig(id, { salva_em: e.target.value })} />
          </div>
          <TimeoutEditor id={id} cfg={cfg} nodeOptions={nodeOptions} patchConfig={patchConfig} />
        </>
      )}

      {d.tipo === 'opcoes' && (
        <OpcoesEditor id={id} cfg={cfg} nodeOptions={nodeOptions} patchConfig={patchConfig} />
      )}

      {d.tipo === 'condicao' && (
        <CondicaoEditor id={id} cfg={cfg} d={d} variaveis={variaveis} nodeOptions={nodeOptions} patchConfig={patchConfig} patchNode={patchNode} />
      )}

      {d.tipo === 'acao' && (
        <AcaoEditor id={id} cfg={cfg} patchConfig={patchConfig} atendentes={atendentes} />
      )}

      {d.tipo === 'subfluxo' && (
        <div className="mb-2">
          <label className="form-label small mb-0">Subfluxo (versao publicada)</label>
          <select
            className="form-select form-select-sm"
            value={cfg.fluxo_versao_id || ''}
            onChange={(e) => {
              const sel = reutilizaveis.find((r) => String(r.versao_id) === e.target.value);
              patchConfig(id, sel ? { fluxo_id: sel.fluxo_id, fluxo_versao_id: sel.versao_id } : { fluxo_id: null, fluxo_versao_id: null });
            }}
          >
            <option value="">(escolher)</option>
            {reutilizaveis.map((r) => <option key={r.versao_id} value={r.versao_id}>{r.nome} (v{r.versao})</option>)}
          </select>
          {reutilizaveis.length === 0 && <small className="text-danger">Nenhum fluxo reutilizavel publicado.</small>}
        </div>
      )}

      {(d.tipo === 'handoff') && (
        <div className="mb-2">
          <label className="form-label small mb-0">Mensagem ao entregar (opcional)</label>
          <textarea className="form-control form-control-sm" rows={2} value={cfg.mensagem || ''} onChange={(e) => patchConfig(id, { mensagem: e.target.value })} />
        </div>
      )}

      {SINGLE_OUT.includes(d.tipo) && (
        <SelectAlvo label="Proximo no" valor={d.next} opcoes={nodeOptions} selfId={id} onChange={(v) => patchNode(id, { next: v })} />
      )}

      <hr />
      <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removerNode(id)}>
        <i className="bi bi-trash me-1" /> Remover no
      </button>
    </div>
  );
}

function OpcoesEditor({ id, cfg, nodeOptions, patchConfig }) {
  const opcoes = cfg.opcoes || [];
  const set = (novas) => patchConfig(id, { opcoes: novas });
  return (
    <>
      <div className="mb-2">
        <label className="form-label small mb-0">Texto do menu</label>
        <textarea className="form-control form-control-sm" rows={2} value={cfg.texto || ''} onChange={(e) => patchConfig(id, { texto: e.target.value })} />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-0">Mensagem se opcao invalida</label>
        <input className="form-control form-control-sm" value={cfg.invalido?.texto || ''} onChange={(e) => patchConfig(id, { invalido: { texto: e.target.value } })} />
      </div>
      <label className="form-label small mb-1 fw-semibold">Opcoes</label>
      {opcoes.map((o, i) => (
        <div key={i} className="border rounded p-2 mb-2">
          <div className="d-flex gap-1 mb-1">
            <input className="form-control form-control-sm" placeholder="rotulo (1)" value={o.rotulo || ''} onChange={(e) => { const n = [...opcoes]; n[i] = { ...o, rotulo: e.target.value }; set(n); }} />
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => set(opcoes.filter((_, j) => j !== i))}>×</button>
          </div>
          <div className="d-flex gap-1 mb-1">
            <input className="form-control form-control-sm" placeholder="salva_em" value={o.salva_em || ''} onChange={(e) => { const n = [...opcoes]; n[i] = { ...o, salva_em: e.target.value }; set(n); }} />
            <input className="form-control form-control-sm" placeholder="valor" value={o.valor || ''} onChange={(e) => { const n = [...opcoes]; n[i] = { ...o, valor: e.target.value }; set(n); }} />
          </div>
          <select className="form-select form-select-sm" value={o.next || ''} onChange={(e) => { const n = [...opcoes]; n[i] = { ...o, next: e.target.value || null }; set(n); }}>
            <option value="">proximo: (nenhum)</option>
            {nodeOptions.filter((nid) => nid !== id).map((nid) => <option key={nid} value={nid}>{nid}</option>)}
          </select>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-secondary w-100 mb-2" onClick={() => set([...opcoes, { rotulo: '', valor: '', salva_em: '', next: null }])}>
        + Opcao
      </button>

      <TimeoutEditor id={id} cfg={cfg} nodeOptions={nodeOptions} patchConfig={patchConfig} />
    </>
  );
}

function AcaoEditor({ id, cfg, patchConfig, atendentes = [] }) {
  const acao = cfg.acao || ACOES[0];
  const params = cfg.params || {};
  const setParam = (patch) => patchConfig(id, { params: { ...params, ...patch } });
  return (
    <>
      <div className="mb-2">
        <label className="form-label small mb-0">Acao</label>
        <select className="form-select form-select-sm" value={acao} onChange={(e) => patchConfig(id, { acao: e.target.value })}>
          {ACOES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      {acao === 'atribuirAtendente' && (
        <>
          <div className="mb-2">
            <label className="form-label small mb-0">Atendente</label>
            <select className="form-select form-select-sm" value={params.atendente || ''} onChange={(e) => setParam({ atendente: e.target.value })}>
              <option value="">(escolher)</option>
              {atendentes.map((a) => <option key={a.nome} value={a.nome}>{a.nome}{a.perfil ? ` — ${a.perfil}` : ''}</option>)}
            </select>
            {atendentes.length === 0 && <small className="text-muted">Nenhum atendente encontrado.</small>}
            {atendentes.length > 0 && !params.atendente && <small className="text-danger">Obrigatorio: escolha um atendente (senao a publicacao falha).</small>}
          </div>
          <div className="mb-2">
            <label className="form-label small mb-0">Mensagem ao cliente (opcional)</label>
            <textarea className="form-control form-control-sm" rows={2} value={params.mensagem || ''} onChange={(e) => setParam({ mensagem: e.target.value })} />
          </div>
        </>
      )}
      {(acao === 'marcarColetaConcluida' || acao === 'handoffAtendente' || acao === 'encaminharFila') && (
        <div className="mb-2">
          <label className="form-label small mb-0">Mensagem (opcional)</label>
          <textarea className="form-control form-control-sm" rows={2} value={params.mensagem || ''} onChange={(e) => setParam({ mensagem: e.target.value })} />
        </div>
      )}
      {acao === 'coletarVariavel' && (
        <div className="d-flex gap-1 mb-2">
          <input className="form-control form-control-sm" placeholder="chave" value={params.chave || ''} onChange={(e) => setParam({ chave: e.target.value })} />
          <input className="form-control form-control-sm" placeholder="valor" value={params.valor || ''} onChange={(e) => setParam({ valor: e.target.value })} />
        </div>
      )}
      {acao === 'categorizarDescricao' && (
        <div className="mb-2">
          <label className="form-label small mb-0">Variavel de origem</label>
          <input className="form-control form-control-sm" placeholder="descricao" value={params.origem || ''} onChange={(e) => setParam({ origem: e.target.value })} />
          <small className="text-muted">Grava titulo + categoria em bot_dados.</small>
        </div>
      )}
    </>
  );
}

// --- Editor de condicao (operandos por dropdown) ----------------------------
const COND_TOKENS = [
  { v: 'horario.dentro', label: 'Horario: dentro do expediente', bool: true },
  { v: 'conversa.encerrada', label: 'Conversa: ja encerrada', bool: true },
  { v: 'conversa.tem_chamado', label: 'Conversa: tem chamado aberto', bool: true },
];

function rotuloOperador(op) {
  return {
    '==': 'e igual a', '!=': 'e diferente de', contem: 'contem',
    vazio: 'esta vazio', preenchido: 'esta preenchido',
  }[op] || op;
}

function CondicaoEditor({ id, cfg, d, variaveis = [], nodeOptions, patchConfig, patchNode }) {
  const esquerda = cfg.esquerda || '';
  const varOpts = (variaveis || []).map((x) => (x && x.chave ? `var.${x.chave}` : null)).filter(Boolean);
  const conhecidas = [...COND_TOKENS.map((t) => t.v), ...varOpts];
  const legado = esquerda && !conhecidas.includes(esquerda);
  const tokenBool = (COND_TOKENS.find((t) => t.v === esquerda) || {}).bool;
  const semDireita = ['vazio', 'preenchido'].includes(cfg.operador);
  return (
    <>
      <div className="mb-2">
        <label className="form-label small mb-0">Comparar</label>
        <select className="form-select form-select-sm" value={esquerda} onChange={(e) => patchConfig(id, { esquerda: e.target.value })}>
          <option value="">(escolher)</option>
          <optgroup label="Variaveis do fluxo">
            {varOpts.length
              ? varOpts.map((v) => <option key={v} value={v}>{v}</option>)
              : <option value="" disabled>nenhuma variavel declarada</option>}
          </optgroup>
          <optgroup label="Contexto da conversa">
            {COND_TOKENS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </optgroup>
          {legado && <option value={esquerda}>{esquerda} (atual)</option>}
        </select>
      </div>
      <div className="mb-2">
        <label className="form-label small mb-0">Condicao</label>
        <select className="form-select form-select-sm" value={cfg.operador || '=='} onChange={(e) => patchConfig(id, { operador: e.target.value })}>
          {OPERADORES.map((o) => <option key={o} value={o}>{rotuloOperador(o)}</option>)}
        </select>
      </div>
      {!semDireita && (
        <div className="mb-2">
          <label className="form-label small mb-0">Valor</label>
          {tokenBool ? (
            <select className="form-select form-select-sm" value={cfg.direita == null ? '' : cfg.direita} onChange={(e) => patchConfig(id, { direita: e.target.value })}>
              <option value="">(escolher)</option>
              <option value="true">Verdadeiro (sim)</option>
              <option value="false">Falso (nao)</option>
            </select>
          ) : (
            <input className="form-control form-control-sm" placeholder="ex: externo" value={cfg.direita || ''} onChange={(e) => patchConfig(id, { direita: e.target.value })} />
          )}
          <small className="text-muted">{tokenBool ? 'Compara com verdadeiro/falso.' : 'Texto exato a comparar (sem aspas).'}</small>
        </div>
      )}
      <SelectAlvo label="Se verdadeiro, ir para" valor={d.then} opcoes={nodeOptions} selfId={id} onChange={(v) => patchNode(id, { then: v })} />
      <SelectAlvo label="Se falso, ir para" valor={d.else} opcoes={nodeOptions} selfId={id} onChange={(v) => patchNode(id, { else: v })} />
    </>
  );
}

// --- Mensagem do simulador --------------------------------------------------
function SimMsg({ m }) {
  const cls = {
    bot: 'fx-msg fx-msg-bot', user: 'fx-msg fx-msg-user',
    anotacao: 'fx-msg fx-msg-anotacao', erro: 'fx-msg fx-msg-erro',
  }[m.tipo] || 'fx-msg fx-msg-bot';
  return <div className={cls}>{m.texto}</div>;
}

// --- Editor de timeout (reusado por opcoes e pergunta) ----------------------
function TimeoutEditor({ id, cfg, nodeOptions, patchConfig }) {
  return (
    <>
      <div className="fx-divider" />
      <label className="form-label small mb-1 fw-semibold">Timeout (sem resposta)</label>
      <div className="d-flex align-items-center gap-2 mb-2">
        <input
          type="number" min="0" className="form-control form-control-sm" style={{ maxWidth: 90 }}
          placeholder="min" value={cfg.timeout_min || ''}
          onChange={(e) => patchConfig(id, { timeout_min: e.target.value ? Number(e.target.value) : undefined })}
        />
        <span className="text-muted" style={{ fontSize: '.78rem' }}>minutos sem responder</span>
      </div>
      {cfg.timeout_min ? (
        <>
          <label className="form-label small mb-0">Ao estourar, ir para</label>
          <select
            className="form-select form-select-sm mb-1" value={cfg.timeout_next || ''}
            onChange={(e) => patchConfig(id, { timeout_next: e.target.value || undefined })}
          >
            <option value="">Encerrar fluxo (larga no pool / fila geral)</option>
            {nodeOptions.filter((nid) => nid !== id).map((nid) => <option key={nid} value={nid}>{nid}</option>)}
          </select>
          <input
            className="form-control form-control-sm" placeholder="Mensagem ao estourar (opcional)"
            value={cfg.timeout_mensagem || ''}
            onChange={(e) => patchConfig(id, { timeout_mensagem: e.target.value || undefined })}
          />
        </>
      ) : null}
    </>
  );
}
